import { useTranslation } from 'react-i18next';
import { COLORS } from '../../themev2';
import Button from '../Button';
import MoreMenu from '../MoreMenu';
import Typography from '../Typography';
import IconButton from '@mui/material/IconButton';
import { DownloadIcon, CrossIcon } from '../../components/Icons';
import Chip from '../Chip';
import InfoButton from '../InfoButton';

interface DocumentGroupProps {
  name: string;
  linkName: string;
  chipText?: string;
  chipColorProps?: { color: string; backgroundColor: string };
  tooltip?: string;
  linkAction: () => void;
  onClickDownload?: () => void;
  onClickNotifyUser?: () => void;
  onClickDelete?: () => void;
}

const DocumentGroup = ({
  name,
  linkName,
  chipText,
  chipColorProps,
  tooltip,
  linkAction,
  onClickDownload,
  onClickNotifyUser,
  onClickDelete,
}: DocumentGroupProps) => {
  const { t } = useTranslation('compliance');
  const onlyDelete = !!onClickDelete && !onClickDownload && !onClickNotifyUser;
  const onlyDownload = !!onClickDownload && !onClickDelete && !onClickNotifyUser;
  const menuActions = [
    { label: t('compliance:documents.actions.download'), onClick: onClickDownload },
    { label: t('compliance:documents.actions.notifyUser'), onClick: onClickNotifyUser },
    {
      label: t('compliance:documents.actions.delete'),
      onClick: onClickDelete,
      color: COLORS.feedback.danger,
    },
  ].reduce<{ label: string; onClick: () => void; color?: string }[]>((acc, action) => {
    if (action.onClick) {
      acc.push(action as { label: string; onClick: () => void; color?: string });
    }
    return acc;
  }, []);

  return (
    <div
      style={{
        display: 'flex',
        padding: '4px 12px 4px 20px',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: COLORS.gray[100],
        borderRadius: '8px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
        <Typography
          variant="body1"
          sx={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            padding: '12px 0',
          }}
        >
          {name}
        </Typography>
        {tooltip && <InfoButton title={tooltip} />}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {chipText && chipColorProps && (
          <Chip
            size="small"
            sx={{ color: chipColorProps.color, backgroundColor: chipColorProps.backgroundColor }}
            label={chipText}
          />
        )}
        <Button type="link" color="secondary" onClick={linkAction}>
          {linkName}
        </Button>
        {onlyDelete || onlyDownload ? (
          <IconButton onClick={onClickDelete || onClickDownload}>
            {onlyDelete ? <CrossIcon /> : <DownloadIcon />}
          </IconButton>
        ) : (
          <MoreMenu items={menuActions} />
        )}
      </div>
    </div>
  );
};

export default DocumentGroup;
